<template>
  <v-container fluid>
    <v-card>
      <v-card-title primary-title>
        <h3 class="headline mb-0">売掛金回収照会</h3>
      </v-card-title>
      <v-card-actions>
        <v-btn
          :disabled="query.yearMonth === null"
          @click="search"
          class="btn-search"
          v-permit.SHARP_FU_WRITEOFF_SEARCH
          >{{ $t("app.buttons.search") }}</v-btn
        >
        <v-btn>戻る</v-btn>
        <table-upload
          @input="foreignUpload"
          title="外貨決済取込"
          v-permit.SHARP_FU_WRITEOFF_USD_UPLOAD
        />
        <table-upload
          @input="jpyUpload"
          title="円貨決済取込"
          v-permit.SHARP_FU_WRITEOFF_JPY_UPLOAD
        />
        <v-btn @click="writeOff" v-permit.SHARP_FU_WRITEOFF_DO_AUTO
          >消込処理</v-btn
        >
        <v-btn @click="excelDownload" v-permit.SHARP_FU_WRITEOFF_XLS_DOWNLOAD
          >違算明細</v-btn
        >
        <v-btn
          :disabled="query.yearMonth === null || !query.yearMonth"
          @click="pdfDownload"
          v-permit.SHARP_FU_WRITEOFF_PDF_DOWNLOAD
          >売掛金回収レポート</v-btn
        >
        <v-btn
          :disabled="!canCreate && !canEdit"
          @click="createEditEiaj"
          v-permit.SHARP_FU_WRITEOFF_EIAJ_NEW
          >伝票登録</v-btn
        >
        <v-btn
          :disabled="!canEdit"
          @click="remove"
          v-permit.SHARP_FU_WRITEOFF_EIAJ_DELETE
          >伝票削除</v-btn
        >
        <v-btn @click="handHedging" v-permit.SHARP_FU_WRITEOFF_DO_MANUAL
          >手動消込</v-btn
        >
        <v-btn
          @click="handHedgingCancel"
          v-permit.SHARP_FU_WRITEOFF_DO_MANUAL_CANCEL
          >手動消込取消</v-btn
        >
      </v-card-actions>
      <v-form>
        <v-container fluid>
          <v-layout row wrap>
            <v-flex md3 sm6 xs6>
              <v-select
                :items="status"
                :multiple="true"
                clearable
                label="回収区分"
                v-model="query.paymentType"
              ></v-select>
            </v-flex>
            <v-flex md3 sm6 xs6>
              <remote-autocomplete
                :label="$t('app.labels.bu.name')"
                item-value="code"
                url="divisions/kv?type=BU"
                v-model="query.buCode"
              />
            </v-flex>
            <v-flex md3 sm6 xs12>
              <v-text-field
                label="EIAJ 伝票No."
                v-model="query.eiajNo"
              ></v-text-field>
            </v-flex>
            <v-flex md3 sm6 xs6>
              <remote-autocomplete
                label="通貨区分"
                url="currencies/kv"
                v-model="query.currencyCode"
              />
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex md3 sm6 xs6>
              <date-picker
                label="請求年月"
                required
                type="month"
                v-model="query.yearMonth"
              ></date-picker>
            </v-flex>
            <v-flex md2 sm2 xs2>
              <v-checkbox
                label="金額差分あり"
                v-model="falseResult"
              ></v-checkbox>
            </v-flex>
            <v-flex md2 sm2 xs2>
              <v-checkbox
                label="金額差分なし"
                v-model="trueResult"
              ></v-checkbox>
            </v-flex>
          </v-layout>
        </v-container>
      </v-form>
    </v-card>
    <v-card>
      <v-toolbar color="white" flat>
        <v-spacer></v-spacer>
        <v-toolbar-title style="width: 20%">請求データ PF</v-toolbar-title>

        <v-toolbar-title style="width: 15%">支払データ SC</v-toolbar-title>

        <v-toolbar-title style="width: 12%">金額差分</v-toolbar-title>
      </v-toolbar>
      <v-data-table
        :headers="headers"
        :items="results"
        :pagination.sync="pagination"
        :rows-per-page-items="[10, 20, 50, 100, 200, 500, 1000]"
        :total-items="pagination.totalItems"
        item-key="id"
        select-all
        v-model="selected[pagination.page]"
      >
        <template slot="items" slot-scope="props">
          <td>
            <v-checkbox
              hide-details
              primary
              v-model="props.selected"
            ></v-checkbox>
          </td>
          <td>{{ requestDate(props.item) }}</td>
          <td>{{ props.item.napinKey }}</td>
          <td>{{ props.item.buCode }}:{{ props.item.buName }}</td>
          <td>{{ props.item.poNo }}</td>
          <td>{{ exchange(props.item) }}</td>
          <td>{{ props.item.currencyCode }}</td>
          <td class="leftVerticalLine" v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.requestTaxedAmount === null
                ? null
                : props.item.requestTaxedAmount | currency({ fractionCount: 0 })
            }}
          </td>
          <td v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.requestUntaxedAmount == null
                ? null
                : props.item.requestUntaxedAmount
                  | currency({ fractionCount: 0 })
            }}
          </td>
          <td v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.requestTax == null
                ? null
                : props.item.requestTax | currency({ fractionCount: 0 })
            }}
          </td>
          <td v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.requestTotalAmount == null
                ? null
                : props.item.requestTotalAmount | currency({ fractionCount: 0 })
            }}
          </td>
          <td class="leftVerticalLine" v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.requestTaxedAmount === null
                ? null
                : props.item.requestTaxedAmount | currency({ fractionCount: 2 })
            }}
          </td>
          <td v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.requestUntaxedAmount == null
                ? null
                : props.item.requestUntaxedAmount
                  | currency({ fractionCount: 2 })
            }}
          </td>
          <td v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.requestTax == null
                ? null
                : props.item.requestTax | currency({ fractionCount: 2 })
            }}
          </td>
          <td v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.requestTotalAmount == null
                ? null
                : props.item.requestTotalAmount | currency({ fractionCount: 2 })
            }}
          </td>
          <!-- <td class="leftVerticalLine" v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.paymentTaxedAmount == null
                ? null
                : props.item.paymentTaxedAmount | currency({ fractionCount: 0 })
            }}
          </td>
          <td v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.paymentUntaxedAmount == null
                ? null
                : props.item.paymentUntaxedAmount
                  | currency({ fractionCount: 0 })
            }}
          </td> 
          <td v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.paymentTax == null
                ? null
                : props.item.paymentTax | currency({ fractionCount: 0 })
            }}
          </td>-->
          <td class="leftVerticalLine">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </td>
          <td>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
          <td>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
          <td>
            {{
              props.item.paymentTotalAmount == null
                ? null
                : props.item.paymentTotalAmount
                  | currency({
                    fractionCount: props.item.currencyCode !== "JPY" ? 2 : 0,
                  })
            }}
          </td>

          <!-- <td class="leftVerticalLine" v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.paymentTaxedAmount == null
                ? null
                : props.item.paymentTaxedAmount | currency({ fractionCount: 2 })
            }}
          </td>
          <td v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.paymentUntaxedAmount == null
                ? null
                : props.item.paymentUntaxedAmount
                  | currency({ fractionCount: 2 })
            }}
          </td>
          <td v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.paymentTax == null
                ? null
                : props.item.paymentTax | currency({ fractionCount: 2 })
            }}
          </td> -->

          <!-- <td class="leftVerticalLine" v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.differenceTaxedAmount == null
                ? null
                : props.item.differenceTaxedAmount
                  | currency({ fractionCount: 0 })
            }}
          </td>
          <td v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.differenceUntaxedAmount == null
                ? null
                : props.item.differenceUntaxedAmount
                  | currency({ fractionCount: 0 })
            }}
          </td>
          <td v-if="props.item.currencyCode === 'JPY'">
            {{
              props.item.differenceTax == null
                ? null
                : props.item.differenceTax | currency({ fractionCount: 0 })
            }}
          </td> -->
          <td class="leftVerticalLine">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </td>
          <td>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
          <td>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</td>
          <td>
            {{
              props.item.differenceTotalAmount == null
                ? null
                : props.item.differenceTotalAmount
                  | currency({
                    fractionCount: props.item.currencyCode !== "JPY" ? 2 : 0,
                  })
            }}
          </td>

          <!-- <td class="leftVerticalLine" v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.differenceTaxedAmount == null
                ? null
                : props.item.differenceTaxedAmount
                  | currency({ fractionCount: 2 })
            }}
          </td>
          <td v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.differenceUntaxedAmount == null
                ? null
                : props.item.differenceUntaxedAmount
                  | currency({ fractionCount: 2 })
            }}
          </td>
          <td v-if="props.item.currencyCode !== 'JPY'">
            {{
              props.item.differenceTax == null
                ? null
                : props.item.differenceTax | currency({ fractionCount: 2 })
            }}
          </td> -->
        </template>
        <template v-slot:footer>
          <td :colspan="14"></td>
          <td>合計：</td>
          <td>{{ totalTaxedAmount }}</td>
          <td>{{ totalUntaxedAmount }}</td>
          <td>{{ totalTaxAmount }}</td>
          <td>{{ totalAmount }}</td>
        </template>
      </v-data-table>
    </v-card>
    <loading-message ref="loading" />
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      trueResult: null,
      falseResult: null,
      totalTaxedAmount: 0,
      totalUntaxedAmount: 0,
      totalTaxAmount: 0,
      totalAmount: 0,
      status: [
        { text: "未消込", value: "UNCLEARED" },
        { text: "消込済", value: "CLEAROUT" },
        { text: "未入金", value: "UNPAYMENT" },
        { text: "過入金", value: "OVERPAYMENT" },
        { text: "純無償", value: "SUBTRACTION" },
        { text: "金额不一致", value: "AMOUNT_NOT_EQUAL" },
      ],
      query: {
        yearMonth: null,
        paymentType: [],
      },
      receivableEIAJ: [],
      expanded: [],
      paymentEIAJ: [],
      selected: {},
      pagination: {
        page: 1,
      },
      results: [],
      headers: [
        { text: "請求年月", sortable: false },
        { text: "納品キー番号", sortable: false },
        { text: "事業本部", sortable: false },
        { text: "P/O CD", sortable: false },
        { text: "伝票区分", sortable: false },
        { text: "通貨", sortable: false },
        { text: "課税金額", sortable: false },
        { text: "非課税金額", sortable: false },
        { text: "税金", sortable: false },
        { text: "税込金額", sortable: false },
        { text: "       ", sortable: false },
        { text: "       ", sortable: false },
        { text: "       ", sortable: false },
        { text: "税込金額", sortable: false },
        { text: "", sortable: false },
        { text: "", sortable: false },
        { text: "", sortable: false },
        { text: "税込金額", sortable: false },
      ],
    };
  },
  methods: {
    requestDate(item) {
      return (
        item.archiveYear +
        "/" +
        (item.archiveMonth >= 10 ? item.archiveMonth : "0" + item.archiveMonth)
      );
    },
    remove() {
      this.$confirm({
        content: this.$t("app.messages.removeConfirm"),
      }).then(() => {
        this.$http.get(`arap/write-off/remove/${this.chosen.id}`).then(() => {
          this.$message("", "削除完了");
          this.load();
        });
      });
    },
    createEditEiaj() {
      if (this.selectedList.length === 0) {
        this.$router.push("/B1401");
      } else {
        this.$router.push({
          path: "/B1401",
          query: { id: this.chosen.id },
        });
      }
    },
    writeOff() {
      this.$http.put("arap/write-off/batch-write-off").then((res) => {
        if (res.data) {
          this.$message("", "消込処理Success!");
          this.load();
        }
        return true;
      });
    },
    load() {
      // this.params().map(d => console.log(d))
      this.$http
        .get("arap/write-off/write-off-result", this.params())
        .then((res) => {
          this.pagination.page = res.data.number + 1;
          this.pagination.rowsPerPage = res.data.size;
          this.pagination.totalItems = res.data.totalElements;
          this.results = res.data.content.map((r, index) => ({
            ...r,
            seq: index,
          }));
          this.loading = false;
        });
    },
    getDifferenceTotal() {
      this.$http
        .get(`arap/write-off/difference-total`, this.params())
        .then((res) => {
          this.totalTaxedAmount = res.data.taxedAmount;
          this.totalUntaxedAmount = res.data.untaxedAmount;
          this.totalTaxAmount = res.data.taxAmount;
          this.totalAmount = res.data.totalAmount;
        });
    },
    params() {
      this.query = { ...this.query, result: this.result };
      return {
        params: {
          ...this.query,
          paymentType: "",
          paymentTypes: this.query.paymentType.join(","),
          ...this.$pageable(),
        },
      };
    },
    catch(res) {
      if (res.response.status === 500) {
        if (res.response.data.message == "HAND_WRITE_OFF_DATA_NOT_ILLEGAL") {
          this.$message(
            this.$t("app.messages.errors.title"),
            this.$t("app.messages.errors.handWriteOffDataNotIllegal")
          );
        }
      }
    },
    handHedging() {
      this.$http
        .put("arap/write-off/hand-hedging", {
          ids: this.selectedList.map((d) => d.id),
        })
        .then((res) => {
          if (res) {
            this.$message("", "手動消込Success!");
            this.load();
          }
          this.$message("", "手動消込が完了しました。");
        })
        .catch(this.catch);
    },
    handHedgingCancel() {
      this.$http
        .put(`arap/write-off/hand-hedging-cancel?id=${this.selectedList[0].id}`)
        .then((res) => {
          if (res) {
            this.$message("", "手動消込取消Success!");
            this.load();
          }
        })
        .catch(this.catch);
    },
    search() {
      this.pagination.page = 0;
      this.selected = {};
      this.autoload = true;
      this.$storeParams();
      this.loading = true;
      this.results = [];
      this.load();

      this.getDifferenceTotal();
      // this.$refs.billing.load()
      // this.$refs.payment.load()
    },
    exchange(item) {
      switch (item.paymentType) {
        case "UNCLEARED":
          return "未消込";
        case "CLEAROUT":
          return "消込済";
        case "UNPAYMENT":
          return "未入金";
        case "OVERPAYMENT":
          return "過入金";
        case "RETURNPAYMENT":
          return "戻入金";
        case "SUBTRACTION":
          return "純無償";
        case "AMOUNT_NOT_EQUAL":
          return "金额不一致";
      }
    },
    excelDownload() {
      this.$download(
        `arap/write-off/write-off?yearMonth=${this.query.yearMonth}`
      );
    },
    pdfDownload() {
      var code;
      if (!this.query.buCode) {
        code = "all";
      } else {
        code = this.query.buCode;
      }
      this.$download(
        `arap/write-off/${this.query.yearMonth}/${code}/writeoff.pdf`
      );
    },
    add() {
      this.$router.push("/B1401");
    },
    callSetData() {
      this.$refs.billing.setData();
      this.$refs.payment.setData();
    },
    setReceivable(item) {
      this.receivableEIAJ = item;
    },
    setPayment(item) {
      this.paymentEIAJ = item;
    },

    clear() {
      this.callSetData();
      this.manualClear();
    },

    foreignUpload(data) {
      data = data.filter(
        (d) => d[8] != null && d[35] != null && d[46] != null && d[47] != null
      );
      var foreignData = [];
      for (var i = 1; i < data.length; i++) {
        let fData = {
          yearMonth: data[i][0],
          buCode: data[i][1],
          loanClassification: data[i][8],
          productName: data[i][30],
          currencyCode: data[i][35],
          quantity: data[i][43],
          foreignCurrencyUnitPrice: data[i][44],
          napinNo: data[i][77],
          foreignAmount2: data[i][115],
          foreignTaxAmount: data[i][117],
        };
        foreignData.push(fData);
      }
      // console.log(foreignData)
      this.$http
        .post("arap/write-off/foreign-file", foreignData)
        .then(() => {
          this.$message("", "アップロードが完了しました。");
        })
        .catch(() => {
          this.$message("", "アップロードが失敗しました。");
        });
    },
    jpyUpload(data) {
      data = data.filter(
        (d) =>
          d[6] != null &&
          d[14] != null &&
          d[16] != null &&
          d[16] != "" &&
          d[17] != null &&
          d[17] != ""
      );
      var jpyData = [];
      for (var i = 1; i < data.length; i++) {
        let jData = {
          poNo: data[i][16],
          napinNo: data[i][17],
          taxedAmount: data[i][14],
          basicUnit: data[i][6],
          paymentDate: this.transferDate(data[i][10]),
        };
        jpyData.push(jData);
      }
      console.log(jpyData);
      this.$http
        .post("arap/write-off/jpy-file", jpyData)
        .then(() => {
          this.$message("", "アップロードが完了しました。");
        })
        .catch(() => {
          this.$message("", "アップロードが失敗しました。");
        });
    },
    transferDate(data) {
      let dt = new Date();
      dt.setFullYear(1900);
      dt.setMonth(0);
      dt.setDate(1);
      dt.setTime(dt.getTime() + (data - 2) * 24 * 3600 * 1000);
      return dt.format("yyyy-MM-dd");
    },
  },
  mounted() {
    this.$loadParams();
  },
  computed: {
    selectedList() {
      return [].concat(...Object.values(this.selected));
    },
    chosen() {
      var arr = [].concat(...Object.values(this.selected));
      return arr[0];
    },
    result() {
      let result =
        (this.trueResult === true &&
          (this.falseResult === null || this.falseResult === false)) === true
          ? true
          : (this.trueResult === null || this.trueResult === false) &&
            this.falseResult === true
          ? false
          : null;
      return result;
    },

    canCreate() {
      return this.selectedList.length === 0;
    },
    canEdit() {
      return (
        this.selectedList.length === 1 &&
        this.selectedList[0].requestSource === "MANUAL" &&
        this.selectedList[0].paymentSource === "MANUAL"
      );
      // return !this.isFinished && (this.isRequestManual || this.isPaymentManual)
    },
    isPaymentManual() {
      return this.chosen && this.chosen.paymentSource === "MANUAL";
    },
    isRequestManual() {
      return this.chosen && this.chosen.requestSource === "MANUAL";
    },
    isFinished() {
      return this.chosen && this.chosen.paymentType === "CLEAROUT";
    },
    isUnpayment() {
      return this.chosen && this.chosen.paymentType === "UNPAYMENT";
    },
    isOverpayment() {
      return this.chosen && this.chosen.paymentType === "OVERPAYMENT";
    },
  },
  watch: {
    pagination() {
      if (this.autoload) this.load();
    },
  },
};
</script>
<style>
.leftVerticalLine {
  border-left: 1px solid black !important;
}
</style>
